// frontend/src/pages/AdminAssessmentResultsPage.tsx
import React, { useState, useEffect } from 'react';
import authAxios from '../auth/authAxios';
import { Download, Eye, Search, User, Award, FileText, X, Clock, Target, CheckCircle } from 'lucide-react';

interface UserAssessmentResultSummary {
  attempt_id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  assessment_title: string;
  user_role: string;
  role_category: string;
  started_at: string;
  completed_at: string;
  total_score: number;
  max_possible_score: number;
  percentage_score: number;
  status: string;
  time_spent_minutes: number;
  questions_count: number;
  admin_reviewed: boolean;
}

interface UserAssessmentResultDetail extends UserAssessmentResultSummary {
  language_preferences: { [key: string]: string };
  skill_analysis: any;
  admin_notes: string | null;
}

interface QuestionDetail {
  question_id: string;
  question_type: string;
  question_text: string;
  code_snippet: string | null;
  options: string[] | null;
  correct_answer: string;
  explanation: string;
  points: number;
  order_index: number;
  skill_focus: string;
  language: string;
  difficulty: string;
  user_answer: string | null;
  is_correct: boolean;
  points_earned: number;
  ai_feedback: string | null;
  ai_analysis: any;
  response_time_seconds: number;
}

interface AssessmentDetailResponse {
  attempt: UserAssessmentResultDetail;
  questions: QuestionDetail[];
}

interface FilterOptions {
  role_category: string;
  status: string;
  date_range: string;
  search: string;
}

const AdminAssessmentResultsPage: React.FC = () => {
  const [results, setResults] = useState<UserAssessmentResultSummary[]>([]);
  const [filteredResults, setFilteredResults] = useState<UserAssessmentResultSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    role_category: '',
    status: '',
    date_range: '',
    search: ''
  });
  const [selectedResult, setSelectedResult] = useState<UserAssessmentResultDetail | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'analysis' | 'questions'>('overview');
  const [questionsData, setQuestionsData] = useState<QuestionDetail[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  useEffect(() => {
    fetchAssessmentResults();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [results, filters]);

  const fetchAssessmentResults = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get('/api/admin/assessment-results/summary');
      setResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentDetail = async (attemptId: string): Promise<UserAssessmentResultDetail> => {
    const response = await authAxios.get(`/api/admin/assessment-results/${attemptId}`);
    return response.data;
  };

  const fetchQuestionsDetail = async (attemptId: string): Promise<QuestionDetail[]> => {
    const response = await authAxios.get(`/api/admin/assessment-results/${attemptId}/details`);
    return response.data.questions;
  };

  const applyFilters = () => {
    let filtered = [...results];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(result => 
        result.user_name.toLowerCase().includes(searchLower) ||
        result.user_email.toLowerCase().includes(searchLower) ||
        result.assessment_title.toLowerCase().includes(searchLower) ||
        result.user_role.toLowerCase().includes(searchLower)
      );
    }

    // Role category filter
    if (filters.role_category) {
      filtered = filtered.filter(result => result.role_category === filters.role_category);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(result => result.status === filters.status);
    }

    // Date range filter
    if (filters.date_range) {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (filters.date_range) {
        case '7d':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          cutoffDate.setDate(now.getDate() - 90);
          break;
      }
      
      if (filters.date_range !== '') {
        filtered = filtered.filter(result => 
          new Date(result.completed_at) >= cutoffDate
        );
      }
    }

    setFilteredResults(filtered);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleViewDetails = async (result: UserAssessmentResultSummary) => {
    try {
      setLoadingDetail(true);
      setShowDetailModal(true);
      setActiveTab('overview'); // Reset to overview tab
      setQuestionsData([]); // Clear previous questions data

      // Fetch detailed data for this assessment
      const detailedResult = await fetchAssessmentDetail(result.attempt_id);
      setSelectedResult(detailedResult);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load assessment details');
      setShowDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleTabChange = async (tab: 'overview' | 'analysis' | 'questions') => {
    setActiveTab(tab);

    // Lazy load questions data when questions tab is selected
    if (tab === 'questions' && selectedResult && questionsData.length === 0) {
      try {
        setLoadingQuestions(true);
        const questions = await fetchQuestionsDetail(selectedResult.attempt_id);
        setQuestionsData(questions);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load questions');
      } finally {
        setLoadingQuestions(false);
      }
    }
  };

  const handleMarkReviewed = async (attemptId: string) => {
    try {
      await authAxios.patch(`/api/admin/assessment-results/${attemptId}/reviewed`);
      setResults(prev => prev.map(result => 
        result.attempt_id === attemptId 
          ? { ...result, admin_reviewed: true }
          : result
      ));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to mark as reviewed');
    }
  };

  const handleExportResults = async () => {
    try {
      const response = await authAxios.get('/api/admin/assessment-results/export', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-results-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export results');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'abandoned': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Assessment Results Dashboard</h1>
        <p className="text-gray-300">
          View and analyze all user assessment results, generated questions, and AI feedback.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users, roles..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Category Filter */}
          <select
            value={filters.role_category}
            onChange={(e) => handleFilterChange('role_category', e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            <option value="development">Development</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="data">Data Engineering</option>
            <option value="cybersecurity">Cybersecurity</option>
            <option value="leadership">Leadership</option>
            <option value="application_admin">Application Admin</option>
            <option value="business_analysis">Business Analysis</option>
            <option value="support_operations">Support Operations</option>
            <option value="quality_assurance">Quality Assurance</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="expired">Expired</option>
            <option value="abandoned">Abandoned</option>
          </select>

          {/* Date Range Filter */}
          <select
            value={filters.date_range}
            onChange={(e) => handleFilterChange('date_range', e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Time</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>

          {/* Export Button */}
          <button
            onClick={handleExportResults}
            className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>

        <div className="text-sm text-gray-400">
          Showing {filteredResults.length} of {results.length} results
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  User Selected Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredResults.map((result) => (
                <tr key={result.attempt_id} className="hover:bg-gray-700">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-white">{result.user_name}</div>
                        <div className="text-sm text-gray-400">{result.user_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{result.assessment_title.replace('Assessment - ', '')}</div>
                    <div className="text-sm text-gray-400">{result.role_category}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${getScoreColor(result.percentage_score)}`}>
                      {result.percentage_score.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-400">
                      {result.total_score.toFixed(1)}/{result.max_possible_score} pts
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                      {result.status}
                    </div>
                    <div className="text-sm text-gray-400">
                      {result.completed_at ? new Date(result.completed_at).toLocaleDateString() : ''}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(result)}
                        className="text-blue-400 hover:text-blue-300"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!result.admin_reviewed && (
                        <button
                          onClick={() => handleMarkReviewed(result.attempt_id)}
                          className="text-green-400 hover:text-green-300"
                          title="Mark as reviewed"
                        >
                          <Award className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredResults.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Results Found</h3>
          <p className="text-gray-400">
            No assessment results match your current filters.
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-white">Assessment Details</h2>
                {selectedResult && (
                  <p className="text-gray-400 mt-1">{selectedResult.user_name} - {selectedResult.assessment_title}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedResult(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex flex-col h-full">
              {loadingDetail ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  <span className="ml-4 text-white">Loading assessment details...</span>
                </div>
              ) : selectedResult ? (
                <>
                  {/* Tab Navigation */}
                  <div className="border-b border-gray-700">
                    <nav className="flex space-x-8 px-6" aria-label="Tabs">
                      <button
                        onClick={() => handleTabChange('overview')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'overview'
                            ? 'border-blue-500 text-blue-400'
                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        Overview
                      </button>
                      <button
                        onClick={() => handleTabChange('analysis')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'analysis'
                            ? 'border-blue-500 text-blue-400'
                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        Skill Analysis
                      </button>
                      <button
                        onClick={() => handleTabChange('questions')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'questions'
                            ? 'border-blue-500 text-blue-400'
                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        Questions & Answers
                      </button>
                    </nav>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {activeTab === 'overview' && (
                      <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-gray-700 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                              <User className="w-5 h-5" />
                              User Information
                            </h3>
                            <div className="space-y-2 text-sm">
                              <div><span className="text-gray-400">Name:</span> <span className="text-white">{selectedResult.user_name}</span></div>
                              <div><span className="text-gray-400">Email:</span> <span className="text-white">{selectedResult.user_email}</span></div>
                              <div><span className="text-gray-400">Role:</span> <span className="text-white">{selectedResult.user_role}</span></div>
                              <div><span className="text-gray-400">Category:</span> <span className="text-white capitalize">{selectedResult.role_category}</span></div>
                            </div>
                          </div>

                          <div className="bg-gray-700 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                              <Target className="w-5 h-5" />
                              Assessment Results
                            </h3>
                            <div className="space-y-2 text-sm">
                              <div><span className="text-gray-400">Score:</span> <span className={`font-medium ${getScoreColor(selectedResult.percentage_score)}`}>{selectedResult.percentage_score.toFixed(1)}%</span></div>
                              <div><span className="text-gray-400">Points:</span> <span className="text-white">{selectedResult.total_score}/{selectedResult.max_possible_score}</span></div>
                              <div><span className="text-gray-400">Questions:</span> <span className="text-white">{selectedResult.questions_count}</span></div>
                              <div><span className="text-gray-400">Status:</span> <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedResult.status)}`}>{selectedResult.status}</span></div>
                            </div>
                          </div>
                        </div>

                        {/* Timing Info */}
                        <div className="bg-gray-700 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Timing Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div><span className="text-gray-400">Started:</span> <span className="text-white">{new Date(selectedResult.started_at).toLocaleString()}</span></div>
                            <div><span className="text-gray-400">Completed:</span> <span className="text-white">{selectedResult.completed_at ? new Date(selectedResult.completed_at).toLocaleString() : 'N/A'}</span></div>
                            <div><span className="text-gray-400">Time Spent:</span> <span className="text-white">{selectedResult.time_spent_minutes} minutes</span></div>
                          </div>
                        </div>

                        {/* Language Preferences */}
                        {selectedResult.language_preferences && Object.keys(selectedResult.language_preferences).length > 0 && (
                          <div className="bg-gray-700 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-white mb-3">Language Preferences</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              {Object.entries(selectedResult.language_preferences).map(([key, value]) => (
                                <div key={key}>
                                  <span className="text-gray-400 capitalize">{key.replace('_', ' ')}:</span> <span className="text-white">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'analysis' && (
                      <div className="space-y-6">
                        {selectedResult.skill_analysis ? (
                          <div className="bg-gray-700 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-white mb-3">Skill Analysis</h3>
                            <div className="text-sm">
                              <pre className="text-gray-300 whitespace-pre-wrap bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                                {JSON.stringify(selectedResult.skill_analysis, null, 2)}
                              </pre>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <p className="text-gray-400">No skill analysis available for this assessment.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'questions' && (
                      <div className="space-y-6">
                        {loadingQuestions ? (
                          <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-white">Loading questions...</span>
                          </div>
                        ) : questionsData.length > 0 ? (
                          <div className="space-y-4">
                            {questionsData.map((question, index) => (
                              <div key={question.question_id} className="bg-gray-700 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <h4 className="text-lg font-medium text-white">
                                    Question {index + 1}
                                  </h4>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      question.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                      {question.is_correct ? 'Correct' : 'Incorrect'}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                      {question.points_earned}/{question.points} pts
                                    </span>
                                  </div>
                                </div>

                                <div className="space-y-3 text-sm">
                                  {question.explanation && (
                                    <div>
                                      <span className="text-gray-400">Explanation:</span>
                                      <p className="text-white mt-1">{question.explanation}</p>
                                    </div>
                                  )}

                                  <div>
                                    <span className="text-gray-400">Question:</span>
                                    <p className="text-white mt-1">{question.question_text}</p>
                                  </div>

                                  {question.code_snippet && (
                                    <div>
                                      <span className="text-gray-400">Code:</span>
                                      <pre className="bg-gray-800 p-3 rounded mt-1 text-xs overflow-x-auto">
                                        <code>{question.code_snippet}</code>
                                      </pre>
                                    </div>
                                  )}

                                  <div>
                                    <span className="text-gray-400">User Answer:</span>
                                    <p className="text-white mt-1">{question.user_answer || 'No answer provided'}</p>
                                  </div>

                                  {/* <div>
                                    <span className="text-gray-400">Correct Answer:</span>
                                    <p className="text-green-400 mt-1">{question.correct_answer}</p>
                                  </div> */}

                                  {question.ai_feedback && (
                                    <div>
                                      <span className="text-gray-400">AI Feedback:</span>
                                      <p className="text-blue-300 mt-1">{question.ai_feedback}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <p className="text-gray-400">No questions available for this assessment.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Admin Actions */}
                  <div className="border-t border-gray-700 p-6 bg-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectedResult.admin_reviewed ? (
                          <span className="flex items-center gap-2 text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Reviewed by Admin
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              handleMarkReviewed(selectedResult.attempt_id);
                              setShowDetailModal(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm transition-colors"
                          >
                            <Award className="w-4 h-4" />
                            Mark as Reviewed
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setShowDetailModal(false);
                          setSelectedResult(null);
                          setQuestionsData([]);
                          setActiveTab('overview');
                        }}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md text-sm transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAssessmentResultsPage;
